<script lang="ts" setup>
import { Image } from "vant";
import { useAvatarImg, useDefaultAvatar, useRawImageUrl } from "@/use/common";
import { computed } from "vue";
import { useMate } from "@/stores/mate";
import { useGlobalUser } from "@/stores/globalUser";
import { Mate } from "@/types/entity";
import { MateType } from "@/enums/mateEnums";

const props = withDefaults(
  defineProps<{
    mate?: Mate;
    avatarUrl?: string;
    highQuality?: boolean;
    aspect?: string;
    rounded?: boolean;
    isUser?: boolean;
  }>(),
  {
    highQuality: false,
    aspect: "3/4",
    rounded: true,
    isUser: false,
  }
);
const mateStore = useMate();
const userStore = useGlobalUser();
const aspectRatio = computed(() => props.aspect);
const frame = computed(() => {
  if (!props.mate?.user) return false;
  if (mateStore.isMyMate(props.mate?.uuid)) {
    return userStore.user.subscriptionType === 4;
  } else if (props.mate.user) {
    return props.mate.user.subscriptionType === 4;
  } else {
    return false;
  }
});

const url = computed(() => {
  if (props.avatarUrl?.startsWith("/")) return props.avatarUrl;
  let url = props.avatarUrl ?? props.mate?.mediaUrl;
  if (!url) return useDefaultAvatar();
  if (props.highQuality) {
    return useRawImageUrl(url);
  } else if (url.includes(".webp")) {
    return url;
  } else {
    return useAvatarImg(url);
  }
});

const isSquare = computed(() => {
  return (
    props.mate?.type === MateType.SelfAvatar ||
    props.mate?.type === MateType.User ||
    props.isUser
  );
});
</script>
<template>
  <ShineBorder
    :enable-shine="frame"
    :class="[
      'rounded overflow-hidden',
      isSquare ? 'aspect-square rounded-full' : 'aspect-[3/4]',
    ]"
  >
    <div
      :class="[
        'overflow-hidden',
        avatarUrl === '' && 'bg-neutral',
        rounded && 'rounded',
      ]"
      class="user-avatar w-full h-full"
    >
      <Image
        :class="{ 'bg-neutral': !avatarUrl }"
        :src="url"
        class="w-full h-full"
        fit="cover"
        crossorigin="anonymous"
      >
        <template #loading>
          <div class="w-full h-full bg-neutral"></div>
        </template>
      </Image>
    </div>
  </ShineBorder>
</template>
<style scoped>
.user-avatar {
  --aspect-ratio: v-bind(aspectRatio);
  aspect-ratio: var(--aspect-ratio, 3/4);
}
</style>
